import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Doughnut, Bar, getElementAtEvent } from "react-chartjs-2";
import { Button, DatePicker, Select } from "antd";
import AdminJewelryReportService from "../../../services/admin-jewelryReport-service";
import { bucketTopN } from "../../ChartsGroup/DiamondReportsCharts/_shared/bucketTopN";
import CustomLegend from "../../ChartsGroup/DiamondReportsCharts/_shared/CustomLegend";
import ChartState from "../../ChartsGroup/DiamondReportsCharts/_shared/ChartState";
import { donutOptions, barOptions } from "../../ChartsGroup/DiamondReportsCharts/_shared/chartTheme";
import { exportChartPdf } from "../../ChartsGroup/DiamondReportsCharts/_shared/exportChartPdf";
import JewelryDetailSearchTable from "../jewelry/_shared/JewelryDetailSearchTable";
import { extractDealerIds } from "../jewelry/_shared/jewelryHelpers";

const { Option } = Select;
const { RangePicker } = DatePicker;

// Global combined flagship. Scope matches the other 3 Global reports
// (Category/Metal Type/Price) -- Category + Price only, no Vendor/Retailer
// panels. The pre-redesign page already fetched Vendor/Retailer data here
// but never rendered it (dead code, confirmed by reading the source); since
// Global explicitly has no Vendor Clicks/Retailer Clicks reports at all
// (user-confirmed scope), that dead fetch is dropped rather than wired up --
// wiring it in would be *more* Global vendor/retailer surface than exists
// anywhere else in this feature.
const bandStart = (label) => {
  if (!label || label === "Call For Price") return Infinity;
  const match = String(label).replace(/,/g, "").match(/[\d.]+/);
  return match ? Number(match[0]) : Infinity;
};

const parsePriceBand = (label) => {
  if (label === "Call For Price") return { price: "Call For Price", price1: "", price2: "" };
  const [low, high] = label.split("-");
  const lowPrice1 = parseInt(low.replace(/^.*?\$/, "").replace(/,/g, "").trim(), 10);
  const highPrice2 = parseInt((high || "").replace(/[$,]/g, ""), 10);
  return {
    price: "",
    price1: low.includes("Under") ? "0" : lowPrice1,
    price2: low.includes("Under") ? lowPrice1 : highPrice2 || "",
  };
};

const emptyFilter = { category: "", price: "", price1: "", price2: "" };

const GlobalJewelryChartReports = () => {
  const { t } = useTranslation();
  const loginDetials = useSelector((state) => state.loginReducer);
  const [dealerId, setDealerId] = useState();
  const [forDealer, setForDealer] = useState("");
  const [dealerMemberTypeName, setDealerMemberTypeName] = useState("");
  const [dataRangeFrom, setDataRangeFrom] = useState(moment().subtract(30, "days").format("YYYY-MM-DD"));
  const [dataRangeTo, setDataRangeTo] = useState(moment().format("YYYY-MM-DD"));
  const [vendorIds, setVendorIds] = useState([]);
  const [retailerIds, setRetailerIds] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [retailerList, setRetailerList] = useState([]);

  const [categoryData, setCategoryData] = useState([]);
  const [priceData, setPriceData] = useState([]);
  const [chartsLoading, setChartsLoading] = useState(false);
  const [totalClicks, setTotalClicks] = useState(0);

  const [filter, setFilter] = useState(emptyFilter);
  const [refetchToken, setRefetchToken] = useState(0);
  const [pdfLoading, setPdfLoading] = useState(false);

  const categoryChartRef = useRef();
  const priceChartRef = useRef();

  useEffect(() => {
    if (loginDetials?.loginDetials?.responseData?.logInSucceeded) {
      const id = loginDetials.loginDetials.responseData.dealerId;
      setDealerId(id);
      setDealerMemberTypeName(loginDetials.loginDetials.responseData.dealerMemberTypeName);
      handleGetForDealer(id);
    }
    handleGetVendorDropdownData();
    handleGetRetailerDropdownData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGetForDealer = (id) => {
    AdminJewelryReportService.GetForDealer({ dealerID: id }).then((response) => {
      if (response.data.message === "Success") {
        const fd = response.data.responseData.forDealer;
        setForDealer(fd);
        handleLoadAllCharts(id, fd, dataRangeFrom, dataRangeTo, [], []);
      }
    });
  };

  const handleGetVendorDropdownData = () => {
    const id = loginDetials?.loginDetials?.responseData?.dealerId;
    AdminJewelryReportService.GetVendorDropdownList({ dealerId: id, reporttype: "global" }).then((response) => {
      if (response.data.message === "Success") setVendorList(response.data.responseData);
    });
  };

  const handleGetRetailerDropdownData = () => {
    AdminJewelryReportService.GetRetailerDropdownList({ dealerId: 0, reporttype: "global" }).then((response) => {
      if (response.data.message === "Success") setRetailerList(response.data.responseData);
    });
  };

  const handleVendorChange = (values) => {
    setVendorIds(values);
    if (values.length === 0) {
      handleGetRetailerDropdownData();
    } else {
      AdminJewelryReportService.ChangeRetailerList({ vendorIds: extractDealerIds(values).toString() }).then((response) => {
        if (response.data.message === "Success") setRetailerList(response.data.responseData);
      });
    }
  };

  const handleRetailerChange = (values) => {
    setRetailerIds(values);
    if (values.length === 0) {
      handleGetVendorDropdownData();
    } else {
      AdminJewelryReportService.ChangeVendorList({ retailerIds: extractDealerIds(values).toString() }).then((response) => {
        if (response.data.message === "Success") setVendorList(response.data.responseData);
      });
    }
  };

  const handleLoadAllCharts = (dealerIdArg, forDealerArg, fromDate, toDate, vIds, rIds) => {
    setChartsLoading(true);
    const base = {
      dealerID: dealerIdArg,
      forDealer: forDealerArg,
      fromDate: moment(fromDate).format("YYYY-MM-DD"),
      toDate: moment(toDate).format("YYYY-MM-DD"),
      retailerIds: extractDealerIds(rIds).toString(),
      vendorIds: extractDealerIds(vIds).toString(),
    };
    Promise.allSettled([
      AdminJewelryReportService.GetClicksBasedOnCategory(base),
      AdminJewelryReportService.GetClicksBasedOnPrice(base),
    ]).then(([categoryRes, priceRes]) => {
      const okData = (settled) => {
        if (settled.status !== "fulfilled") {
          console.error("Global jewelry chart fetch failed:", settled.reason);
          return [];
        }
        return settled.value.data.message === "Success" ? settled.value.data.responseData || [] : [];
      };
      const category = okData(categoryRes);
      const price = okData(priceRes);
      setCategoryData(category);
      setPriceData(price);
      setTotalClicks(category.reduce((sum, row) => sum + Number(row.count || 0), 0));
      setChartsLoading(false);
    });
  };

  const onDateChange = (dates, dateStrings) => {
    if (dates) {
      setDataRangeFrom(dateStrings[0]);
      setDataRangeTo(dateStrings[1]);
    }
  };

  const handleApplyFilter = () => {
    setFilter(emptyFilter);
    handleLoadAllCharts(dealerId, forDealer, dataRangeFrom, dataRangeTo, vendorIds, retailerIds);
    setRefetchToken((tok) => tok + 1);
  };

  const handleCategoryClick = (category) => setFilter({ ...emptyFilter, category });
  const handlePriceClick = (label) => setFilter({ ...emptyFilter, ...parsePriceBand(label) });

  const handleDownloadChart = async () => {
    setPdfLoading(true);
    const vendorNames = vendorIds.length === 0 ? t("All Vendors") : vendorList.filter((v) => extractDealerIds(vendorIds).includes(v.dealerID.toString())).map((v) => v.dealerCompany).join(", ");
    const retailerNames = retailerIds.length === 0 ? t("All Retailers") : retailerList.filter((v) => extractDealerIds(retailerIds).includes(v.dealerID.toString())).map((v) => v.dealerCompany).join(", ");
    try {
      await exportChartPdf({
        elementId: "pdf-content",
        title: t("Jewelry Chart Report"),
        dealerCompany: loginDetials?.loginDetials?.responseData?.dealerCompany,
        dealerId,
        dataRangeFrom,
        dataRangeTo,
        infoLines: [
          ...(dealerMemberTypeName !== "Vendor" ? [`Vendors: ${vendorNames}`] : []),
          ...(dealerMemberTypeName === "Admin" ? [`Retailers: ${retailerNames}`] : []),
          `Total Clicks: ${totalClicks}`,
        ],
        fileName: "Global-Jewelry-Chart-Report.pdf",
      });
    } finally {
      setPdfLoading(false);
    }
  };

  const categoryChart = useMemo(
    () => bucketTopN(categoryData, { labelKey: "category", valueKey: "count", chartLabel: "Category", othersLabel: t("Others") }),
    [categoryData, t]
  );
  const priceRows = useMemo(() => {
    const list = priceData.filter((item) => Number(item.total) > 0);
    const sorted = [...list].sort((a, b) => bandStart(a.range) - bandStart(b.range));
    const total = sorted.reduce((sum, item) => sum + Number(item.total), 0);
    return sorted.map((item) => ({
      label: item.range,
      value: Number(item.total),
      percentage: total > 0 ? ((Number(item.total) / total) * 100).toFixed(1) : 0,
    }));
  }, [priceData]);
  const priceChartData = [["Price Band", "Count"], ...priceRows.map((r) => [`${r.label} (${r.percentage}%)`, r.value])];

  const makeClickHandler = (ref, rows, onPick) => (event) => {
    if (!ref.current) return;
    const [el] = getElementAtEvent(ref.current, event);
    if (!el) return;
    const label = rows[el.index]?.[0]?.replace(/\s*\([^)]*\)\s*$/, "");
    if (label && label !== t("Others")) onPick(label);
  };

  const topCategory = [...categoryData].sort((a, b) => Number(b.count || 0) - Number(a.count || 0))[0];
  const topPriceBand = [...priceRows].sort((a, b) => b.value - a.value)[0];

  return (
    <div className="pendjewelreq__section form__fields">
      <div className="row magento__mapping">
        <div className="col-md-12">
          <div className="edr-pagehead-row">
            <div className="edr-pagehead">
              <h1 className="edr-pagehead-title">{t("Jewelry Chart Report")}</h1>
              <div className="edr-pagehead-sub">
                {moment(dataRangeFrom).format("MMM D, YYYY")} – {moment(dataRangeTo).format("MMM D, YYYY")}
              </div>
            </div>
            <div className="edr-pagehead-actions">
              <Button className="ghost-btn" onClick={handleDownloadChart} loading={pdfLoading}>
                <i className="fa fa-download"></i> {t("Download Chart")}
              </Button>
              <button className="primary-btn" onClick={handleApplyFilter}>{t("Apply Filter")}</button>
            </div>
          </div>
        </div>

        <div className="col-md-12">
          <div className="row edr-filters">
            <div className="col-md-3">
              <div className="input__block dataRange-input-block datepicker__block">
                <label>{t("Date Range")}</label>
                <RangePicker
                  className="date-range-input"
                  format={"MM-DD-YYYY"}
                  onChange={onDateChange}
                  defaultValue={[moment().subtract(30, "days"), moment()]}
                  defaultPickerValue={[moment().subtract(30, "days"), moment()]}
                />
              </div>
            </div>
            {dealerMemberTypeName === "Admin" && (
              <>
                <div className="col-md-3">
                  <div className="input__block">
                    <label>{t("Vendor(s)")}</label>
                    <Select className="border__grey" showSearch showArrow mode="multiple" placeholder={t("Select One or More Vendors")} optionFilterProp="children" value={vendorIds} onChange={handleVendorChange}>
                      {vendorList.map((item) => (
                        <Option key={`vendor-${item.dealerID}`} value={`${item.dealerID}|${item.dealerCompany}`}>{item.dealerCompany}</Option>
                      ))}
                    </Select>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="input__block">
                    <label>{t("Retailer(s)")}</label>
                    <Select className="border__grey" showSearch showArrow mode="multiple" placeholder={t("Select one or more retailer")} optionFilterProp="children" value={retailerIds} onChange={handleRetailerChange}>
                      {retailerList.map((item) => (
                        <Option key={`retailer-${item.dealerID}`} value={`${item.dealerID}|${item.dealerCompany}`}>{item.dealerCompany}</Option>
                      ))}
                    </Select>
                  </div>
                </div>
              </>
            )}
            {dealerMemberTypeName === "Vendor" && (
              <div className="col-md-3">
                <div className="input__block">
                  <label>{t("Retailer(s)")}</label>
                  <Select className="border__grey" showSearch showArrow mode="multiple" placeholder={t("Select one or more retailer")} optionFilterProp="children" value={retailerIds} onChange={handleRetailerChange}>
                    {retailerList.map((item) => (
                      <Option key={`retailer-${item.dealerID}`} value={`${item.dealerID}|${item.dealerCompany}`}>{item.dealerCompany}</Option>
                    ))}
                  </Select>
                </div>
              </div>
            )}
            {dealerMemberTypeName !== "Admin" && dealerMemberTypeName !== "Vendor" && (
              <div className="col-md-3">
                <div className="input__block">
                  <label>{t("Vendor(s)")}</label>
                  <Select className="border__grey" showSearch showArrow mode="multiple" placeholder={t("Select one or more vendor")} optionFilterProp="children" value={vendorIds} onChange={handleVendorChange}>
                    {vendorList.map((item) => (
                      <Option key={`vendor-${item.dealerID}`} value={`${item.dealerID}|${item.dealerCompany}`}>{item.dealerCompany}</Option>
                    ))}
                  </Select>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="col-md-12">
          <div className="edr-kpis">
            <div className="edr-kpi">
              <div className="edr-kpi-lab">{t("Total Clicks")}</div>
              <div className="edr-kpi-val">{chartsLoading ? "—" : totalClicks.toLocaleString()}</div>
            </div>
            <div className="edr-kpi">
              <div className="edr-kpi-lab">{t("Top Category")}</div>
              <div className="edr-kpi-val">{topCategory?.category ?? "—"}</div>
              {topCategory && <div className="edr-kpi-sub">{Number(topCategory.count).toLocaleString()} {t("clicks")}</div>}
            </div>
            <div className="edr-kpi">
              <div className="edr-kpi-lab">{t("Price Sweet Spot")}</div>
              <div className="edr-kpi-val">{topPriceBand?.label ?? "—"}</div>
              {topPriceBand && <div className="edr-kpi-sub">{topPriceBand.value.toLocaleString()} {t("clicks")}</div>}
            </div>
          </div>
        </div>

        {topCategory && (
          <div className="col-md-12">
            <div className="edr-insight">
              💡 {t("Demand profile this period")}: {t("shoppers gravitate to")} <b>{topCategory.category}</b>
              {topPriceBand && <> {t("in the")} <b>{topPriceBand.label}</b> {t("band")}</>}.
            </div>
          </div>
        )}

        <div className="col-md-12" id="pdf-content">
          <div className="row">
            <div className="col-lg-6">
              <div className="edr-panel mt-4">
                <div className="edr-panel-head">
                  <label><b>{t("Jewelry Clicks Based on Category")}</b></label>
                </div>
                <div className="chart__section" style={{ display: "flex", justifyContent: "center", flexDirection: "column" }}>
                  {!chartsLoading && categoryChart.chartData.length > 1 ? (
                    <>
                      <CustomLegend chartData={categoryChart.chartData} colorData={categoryChart.colors} />
                      <div className="edr-chartwrap edr-tall">
                        <Doughnut
                          ref={categoryChartRef}
                          data={{
                            labels: categoryChart.chartData.slice(1).map((r) => r[0]),
                            datasets: [{ data: categoryChart.chartData.slice(1).map((r) => r[1]), backgroundColor: categoryChart.colors, borderColor: "#fff", borderWidth: 2 }],
                          }}
                          options={donutOptions}
                          onClick={makeClickHandler(categoryChartRef, categoryChart.chartData.slice(1), handleCategoryClick)}
                        />
                      </div>
                    </>
                  ) : (
                    <ChartState loading={chartsLoading} />
                  )}
                </div>
              </div>
            </div>

            <div className="col-lg-6">
              <div className="edr-panel mt-4">
                <div className="edr-panel-head">
                  <label><b>{t("Jewelry Clicks Based on Price")}</b></label>
                </div>
                <div className="chart__section" style={{ display: "flex", justifyContent: "center", flexDirection: "column" }}>
                  {!chartsLoading && priceRows.length > 0 ? (
                    <div className="edr-chartwrap edr-tall">
                      <Bar
                        ref={priceChartRef}
                        data={{
                          labels: priceChartData.slice(1).map((r) => r[0]),
                          datasets: [{ data: priceChartData.slice(1).map((r) => r[1]), backgroundColor: "#3b5bdb", borderRadius: 4 }],
                        }}
                        options={barOptions(false)}
                        onClick={makeClickHandler(priceChartRef, priceChartData.slice(1), handlePriceClick)}
                      />
                    </div>
                  ) : (
                    <ChartState loading={chartsLoading} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-12">
          <JewelryDetailSearchTable
            dealerId={dealerId}
            forDealer={forDealer}
            dataRangeFrom={dataRangeFrom}
            dataRangeTo={dataRangeTo}
            vendorIds={vendorIds}
            retailerIds={retailerIds}
            dealerMembershipType={dealerMemberTypeName}
            filterParamName={filter.category ? "category" : "price"}
            filterValue={filter.category || filter.price}
            price1={filter.price1}
            price2={filter.price2}
            fileNamePart="GlobalJewelryChartReports"
            dealerCompany={loginDetials?.loginDetials?.responseData?.dealerCompany}
            refetchToken={refetchToken}
            reportType="Global"
            title={t("Product Click Log")}
          />
        </div>
      </div>
    </div>
  );
};

export default GlobalJewelryChartReports;
